import React, { useMemo } from "react"
import { BaseButton, Button, ButtonProps, Text } from "../../vanilla"
import { Hit } from "../../store/ProductStore"
import { getDisabledButtonText, isServer } from "../../utils/utils"
import { Link } from "../link"
import { useContentStore } from "../../store/hooks/useStore"
import { Nullable } from "../../types/utils"

interface ViewAlternativeButtonProps extends ButtonProps {
  product: Hit,
  displayPrice: Nullable<number>,
  isPLPTile?: boolean
  isPDPPage?: boolean
  disabledButtonOverride?: JSX.Element
}

export const ViewAlternativesButton = (props: ViewAlternativeButtonProps) => {
  const {
    product,
    displayPrice,
    isPLPTile,
    isPDPPage,
    disabledButtonOverride,
    ...rest
  } = props

  const { routeMap } = useContentStore()

  const shouldViewAlternatives = !displayPrice 
                            || !product.availableForDayAtStore 
                            || product.isAddToCartDisabled

  // The view alternatives path is derived from the path to the most specific enabled primary sub category
  const viewAlternativesPath = useMemo(() => {
    if (!shouldViewAlternatives) {
      return undefined
    }

    const primaryCategoryId = (product.primaryCategoryId || product.primary_category_id) as string

    if (!primaryCategoryId) {
      return undefined
    }

    const primaryCategory = product.categories?.find(i => i[0].id === primaryCategoryId);

    if (!primaryCategory || primaryCategory.length < 2) {
      return undefined
    }

    // Create fallback categories mapping
    // Assigns index to category id key to inform specificity of category and is used for fallback priority
    const fallbackCategories = primaryCategory.reduce((prev, curr, index, arr) => {
      // Skip adding primary category id and level 1 category
      if (index === 0 || index === arr.length - 1) {
        return prev
      }

      prev[curr.id] = index
      return prev
    }, {} as Record<string, number>)

    let fallbackPriority, fallbackPath

    for (const categoryRoute in routeMap) {
      const [_, contentType, contentId] = routeMap[categoryRoute].split('|')

      if (contentType !== 'CATEGORY') {
        continue
      }

      // Return early if a path matching the primaryCategoryId is found
      if (contentId === primaryCategoryId) {
        return categoryRoute
      }

      // Check if path matches any category in the primary categories array
      // Only update fallback path if category is more specific than current fallback path category
      if (contentId in fallbackCategories && (fallbackPriority === undefined || fallbackCategories[contentId] < fallbackPriority)) {
        fallbackPath = categoryRoute
        fallbackPriority = fallbackCategories[contentId]
      }
    }

    return fallbackPath
  }, [routeMap, shouldViewAlternatives, product.primaryCategoryId, product.primary_category_id])

  if (shouldViewAlternatives && viewAlternativesPath && !isServer && window.location.pathname !== viewAlternativesPath) {
    return (<BaseButton
      {...(rest as any)}
      variant='outlinePrimary'
      size={(isPDPPage || isPLPTile) ? "smWide" : "sm"}
      paddingY={(isPDPPage || isPLPTile) ? "8px" : "0px"}
      as={Link}
      href={viewAlternativesPath}
    >
      <Text variant="unstyled" fontSize={"sm"} display='block'>View Alternatives</Text>
    </BaseButton>)
  }

  if (disabledButtonOverride) {
    return disabledButtonOverride
  }

  return (
    <Button disabled variant="secondary" size='smWide' width={['auto', '150px']}>
      {getDisabledButtonText(product.disabledStatus)}
    </Button>
  )
}
